import React from 'react';
import * as echarts from 'echarts/core';
import ReactECharts from 'echarts-for-react';
import { LineChart } from 'echarts/charts';
import { GridComponent } from 'echarts/components';
import { getBalanceNetFlows } from '../../../apis/vitusApi';
import { CircularProgress, Grid, Typography } from '@material-ui/core';
import { createSpinner } from '../../../utils/spinnerManager';
import { IBalanceNetFlowsResponse } from '../../../apis/vitusApiTypes';
import VChart from '../../../components/VChart/VChart';
import { handleApiError, months } from '../../../utils/common';
import AlertManager from '../../../utils/alertManager';
import messages from '../../../utils/messages';
import Flag from 'react-world-flags';
import { colors, defaultLineRangeChartOptions, isRange, ITabProps, rangeLineColor, rangeLineOptions } from './gasBalanceUtils';
import { isEmpty } from 'lodash';
import GasFlows from './GasFlows';

echarts.use(
    [LineChart, GridComponent]
);

interface IState {
    countriesData: IBalanceNetFlowsResponse["success"]["net_flows"]["countries"],
    showLoading: boolean,
}

class NetFlows extends React.Component<ITabProps, IState> {
    state: IState = {
        countriesData: [],
        showLoading: false,
    }

    componentDidMount() {
        // TODO: commented until flow data performance is improved
        // if (!this.props.data)
        //     this.getData();
        // else
        //     this.storeData(this.props.data);

        if (this.props.data)
            this.storeData(this.props.data);
        else if (!this.state.showLoading)
            this.setState({ showLoading: true });
    }

    componentDidUpdate(prevProps: GasFlows["props"]) {
        if (this.props.data && prevProps.data !== this.props.data)
            this.storeData(this.props.data);
        else if (!this.props.data && !this.state.showLoading)
            this.setState({ showLoading: true });
    }

    getData() {
        const spinner = createSpinner();

        getBalanceNetFlows().then(response => {
            if (response.data.success) {
                this.storeData(response.data.success);
                this.props.onDataChanged(response.data.success);
            }

            if (response.data.error)
                AlertManager.showError(messages.UNEXPECTED_ERROR_OCCURED); //TODO: message

        }).catch(error => {
            handleApiError(error);
        }).finally(() => {
            spinner.hide()
        });
    }

    storeData(response: IBalanceNetFlowsResponse["success"]) {
        this.setState({
            countriesData: response.net_flows.countries || [],
            showLoading: false,
        });
    }

    render() {
        return (
            <Grid container justify="flex-start" alignItems="flex-end">
                {
                    this.state.showLoading &&
                    <div style={{
                        position: "absolute",
                        display: "inline-block",
                        left: "50%",
                        top: "40%",
                        textAlign: 'center',
                    }}>
                        <CircularProgress style={{}} color="inherit" />
                        <Typography>Getting flow data</Typography>
                    </div>
                }
                {
                    !isEmpty(this.state.countriesData) &&
                    this.state.countriesData.map((country, idx) => {
                        return (
                            <ChartRow
                                key={`row_${country.country_name}`}
                                country_name={country.country_name}
                                country_code={country.country_code}
                                description={country.description}
                                data={country.data}
                            />
                        );
                    })
                }
            </Grid>
        )
    }
}

type IChartRowProps = IBalanceNetFlowsResponse["success"]["net_flows"]["countries"][0];

class ChartRow extends React.Component<IChartRowProps, {}> {
    lineRangeChartRef: ReactECharts | null = null;

    componentDidMount() {
        if (this.props.data)
            this.updateLineChart();
    }

    componentDidUpdate(prevProps: IChartRowProps) {
        if (this.props.data && prevProps.data !== this.props.data)
            this.updateLineChart();
    }


    updateLineChart = () => {
        const lineChartInstance = this.lineRangeChartRef?.getEchartsInstance();
        lineChartInstance.showLoading();

        const data = this.props.data;

        lineChartInstance.clear();

        const lines = Object.keys(data[0]).filter(k => k !== "Month" && k !== "Day")

        const dataset = {
            dimensions: ['date_value', ...lines],
            source: data.map(originalRow => {
                const row = { ...originalRow };

                row["date_value"] = `${('0' + row.Month).slice(-2)}_${('0' + row.Day).slice(-2)}`;
                row["month"] = months[row.Month - 1];
                row["day"] = row.Day;
                return row;
            }),
        }

        lineChartInstance.setOption({
            ...defaultLineRangeChartOptions,
            color: colors.map((color, idx) => (isRange(lines[idx]) ? rangeLineColor : color)),
            series: lines.map(l => ({ type: 'line', ...(isRange(l) ? rangeLineOptions : {}) })),
            dataset: dataset,
            legend: {}
        });

        lineChartInstance.hideLoading();
    }


    render() {
        return (
            <Grid item xs={12}>
                <VChart
                    titleIcon={<Flag style={{ margin: 5 }} height="16" code={this.props.country_code} />}
                    title={this.props.description}
                    height={350}
                    report={
                        <ReactECharts
                            ref={(e) => { this.lineRangeChartRef = e; }}
                            echarts={echarts}
                            option={defaultLineRangeChartOptions}
                            notMerge={true}
                            lazyUpdate={true}
                            style={{ width: '99%' }}
                        />
                    } >
                </VChart>
            </Grid>
        );
    }
}

export default NetFlows;